import { getFeaturePanelDefinition } from "../feature-panel-registry.ts";
import type { RuntimeFeatureModule } from "../runtime-feature-registry.ts";
import {
  applySimplificationEffectValue,
  resetSimplificationEffectValue,
} from "../shared/page-simplification-effects.ts";

const attribute = "data-migrosone-a11y-pause-animations";
const motionVariables = [
  "--migrosone-a11y-animation-play-state",
  "--migrosone-a11y-animation-duration",
  "--migrosone-a11y-animation-delay",
  "--migrosone-a11y-transition-duration",
  "--migrosone-a11y-transition-delay",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readBooleanDefault(config: unknown) {
  if (!isRecord(config)) {
    return false;
  }

  if (typeof config.enabledByDefault === "boolean") {
    return config.enabledByDefault;
  }

  return typeof config.defaultEnabled === "boolean" ? config.defaultEnabled : false;
}

function readIncludeTransitions(config: unknown) {
  return isRecord(config) && typeof config.includeTransitions === "boolean"
    ? config.includeTransitions
    : true;
}

function resolveEnabled(config: unknown, preference: unknown) {
  return typeof preference === "boolean" ? preference : readBooleanDefault(config);
}

function resetMotionVariables(document: Document) {
  for (const variable of motionVariables) {
    resetSimplificationEffectValue({ attribute, document, variable });
  }
}

function applyMotionStyle(document: Document, config: unknown) {
  applySimplificationEffectValue({
    attribute,
    document,
    value: "paused",
    variable: "--migrosone-a11y-animation-play-state",
  });
  applySimplificationEffectValue({
    attribute,
    document,
    value: "0.001s",
    variable: "--migrosone-a11y-animation-duration",
  });
  applySimplificationEffectValue({
    attribute,
    document,
    value: "0s",
    variable: "--migrosone-a11y-animation-delay",
  });

  if (!readIncludeTransitions(config)) {
    return;
  }

  applySimplificationEffectValue({
    attribute,
    document,
    value: "0s",
    variable: "--migrosone-a11y-transition-duration",
  });
  applySimplificationEffectValue({
    attribute,
    document,
    value: "0s",
    variable: "--migrosone-a11y-transition-delay",
  });
}

const definition = getFeaturePanelDefinition("pause-animations");

if (!definition) {
  throw new Error("Missing pause-animations feature definition");
}

export const pauseAnimationsFeatureModule: RuntimeFeatureModule = {
  key: "pause-animations",
  definition,
  isSupported({ document }) {
    return Boolean(document.head && document.body && document.documentElement);
  },
  renderControl({ config, document, onPreferenceChange, preference }) {
    const wrapper = document.createElement("label");
    const input = document.createElement("input");
    const text = document.createElement("span");

    wrapper.className = "migrosone-a11y-widget-feature-control";
    input.type = "checkbox";
    input.checked = resolveEnabled(config, preference);
    text.textContent = "Animasyonları duraklat";

    input.addEventListener("change", () => {
      onPreferenceChange(input.checked);
    });

    wrapper.append(input, text);
    return wrapper;
  },
  apply({ config, document, preference }) {
    resetMotionVariables(document);
    if (!resolveEnabled(config, preference)) {
      return;
    }

    applyMotionStyle(document, config);
  },
  reset({ config, document }) {
    resetMotionVariables(document);
    if (readBooleanDefault(config)) {
      applyMotionStyle(document, config);
    }
  },
};
